import { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stepper from '../Stepper';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Button, Link, Snackbar } from '@mui/material';
import { ISingInResponse, useOtpVerifyMutation, useSingInMethodMutation } from '../../../services/singnin.service';
import PageLoading from '../Loading';
import TextfieldOneTimePassword from '../TextfieldOneTimePassword';
import { RootState } from '../../../store';
import { useSelector } from 'react-redux';
import { dynamicCustomEventTracking } from '../../../lib/gtm';
import useCreateJourneyRecurrentUser from '../../../domain/useCreateJourneyRecurrent';
import AlertMessage from '../../atoms/AlertMessage';
import { useRedirectToLandingPage } from '../../../domain/useRedirectToLandingPage';
import { useCookies } from 'react-cookie';
import { cookieShareDomain } from '../../../services/api.service';

type ISingInMethod = ISingInResponse & {
  method: string;
};

type OpenTimePasswordMethodProps = {
  data: ISingInMethod;
};

const OpenTimePasswordMethod = ({ data }: OpenTimePasswordMethodProps) => {
  return (
    <>
      <Typography sx={{ mb: 1 }} variant="body2R">
        Suas informações de agendamento serão automaticamente preenchidas.
      </Typography>
      <Typography sx={{ mb: 1 }} variant="body2R">
        Nós enviamos seu código para <strong>{data.method === 'phone' ? data.whatsapp : data.email}</strong>
      </Typography>
    </>
  );
};

const ToogleMethod = ({ method, onClick }: { method: string; onClick: () => void }) => {
  if (method === 'phone')
    return (
      <Button variant="inline" color="primary" onClick={onClick}>
        Enviar código por email
      </Button>
    );
  return (
    <Button variant="inline" color="primary" onClick={onClick}>
      Enviar código por WhatsApp
    </Button>
  );
};

const OpenTimePasswordForm = () => {
  const { product } = useSelector((state: RootState) => state);
  const navigate = useNavigate();
  const location = useLocation();
  const [singnIn, setSingnIn] = useState<ISingInMethod>(() => {
    const singnInDefaultState: ISingInMethod = {
      oyanaid: '',
      email: '',
      whatsapp: '',
      method: 'phone'
    };

    try {
      if (location.state && location.state.dataMethod) {
        return {
          ...location.state.dataMethod,
          method: 'phone'
        };
      }
      return singnInDefaultState;
    } catch (error) {
      console.error('Error initializing state:', error);
      return singnInDefaultState;
    }
  });
  const [otp, setOtp] = useState<string>('');
  const [singInMethod, { data: dataMethod, error: errorMethod, isLoading: isLoadingMethod }] = useSingInMethodMutation();
  const [otpVerify, { data: dataOTP, error: errorOTP, isLoading: isLoadingOTP }] = useOtpVerifyMutation();
  const { handleCreateJourneyRecurrent, errorJourney, isLoadingJourney, hasSessionStorage } =
    useCreateJourneyRecurrentUser();
  const { handleRedirectToLandingPage } = useRedirectToLandingPage();
  const [openSnackBar, setOpenSnackBar] = useState<{ [key: string]: boolean } | undefined>(undefined);
  const [cookies, setCookie] = useCookies(['oya_oyana_access_token']);

  const continueWithoutLogin = () => {
    dynamicCustomEventTracking.send({ dynamicEventName: 'journey_quick_access_continue_without_login_ag' });
    navigate('/dados-pessoais');
  };
  const sendCodeBy = (method: string) => {
    dynamicCustomEventTracking.send({ dynamicEventName: `journey_quick_access_${method}_ag` });
    setSingnIn({
      ...singnIn,
      method: singnIn.method === 'phone' ? 'email' : 'phone'
    });

    singInMethod({
      id: singnIn.oyanaid,
      method
    });
  };
  const retryCode = (method: string) => {
    dynamicCustomEventTracking.send({ dynamicEventName: `journey_quick_access_${method}_retry_ag` });
    singInMethod({
      id: singnIn.oyanaid,
      method
    });
  };

  useEffect(() => {
    if (otp && otp.length === 6) {
      singInMethod({
        id: singnIn.oyanaid,
        method: singnIn.method
      });
    }
  }, []);

  useEffect(() => {
    const isValid = typeof otp !== 'undefined' && otp.length === 6;
    if (isValid) {
      otpVerify({
        oyanaid: singnIn.oyanaid,
        code: otp
      });
    }
  }, [otp]);

  useEffect(() => {
    if (dataMethod) {
      setOpenSnackBar({ successMethod: true });
    }
  }, [dataMethod]);

  useEffect(() => {
    if (errorOTP) {
      setOpenSnackBar({ errorOTP: true });
    }
    if (errorMethod) {
      setOpenSnackBar({ errorMethod: true });
    }
  }, [errorOTP, errorMethod]);

  useEffect(() => {
    if (dataOTP && product.isNeedAddress) {
      dynamicCustomEventTracking.send({ dynamicEventName: `journey_quick_access_address_ag` });
      navigate('/dados-pessoais/endereco', { state: { token: dataOTP.token } });
      return;
    }
    if (dataOTP && dataOTP.token) {
      setCookie('oya_oyana_access_token', dataOTP.token, {
        domain: cookieShareDomain,
        secure: process.env.REACT_APP_STAGE !== 'local',
        maxAge: 86400
      });
      handleCreateJourneyRecurrent({
        token: dataOTP.token
      });
      return;
    }
  }, [dataOTP, product.isNeedAddress]);

  useEffect(() => {
    if (errorOTP) {
      dynamicCustomEventTracking.send({ dynamicEventName: `journey_quick_access_error_otp_ag` });
    }
  }, [errorOTP]);

  if (!hasSessionStorage) {
    return (
      <Grid container>
        <Grid item xs={12}>
          <AlertMessage
            text={'Sua sessão expirou. Por favor, comece o fluxo novamente para continuar seu agendamento.'}
            textButton="Entendi"
            type="error"
            handler={() => {
              handleRedirectToLandingPage();
            }}
          />
        </Grid>
      </Grid>
    );
  }
  return (
    <Grid container>
      <Grid item xs={12}>
        {(isLoadingMethod || isLoadingOTP || isLoadingJourney) && <PageLoading />}

        <Snackbar
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          open={!!errorOTP && openSnackBar && openSnackBar['errorOTP']}
          autoHideDuration={4000}
          onClose={() => {
            setOpenSnackBar({ errorOTP: false });
          }}
        >
          <Alert severity="error" variant="filled" sx={{ width: '100%' }}>
            <Typography variant="body1">Código inválido</Typography>
          </Alert>
        </Snackbar>
        <Snackbar
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          open={!!errorMethod && openSnackBar && openSnackBar['errorMethod']}
          message="Erro ao enviar o código"
          autoHideDuration={6000}
          onClose={() => {
            setOpenSnackBar({ errorMethod: false });
          }}
        />
        <Snackbar
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          open={dataMethod && openSnackBar && openSnackBar['successMethod']}
          message="Código enviado com sucesso"
          autoHideDuration={6000}
          onClose={() => {
            setOpenSnackBar({ dataMethod: false });
          }}
        />

        {errorJourney && (
          <AlertMessage
            text={'Não conseguimos criar sua jornada, tente novamente mais tarde.'}
            textButton="Entendi"
            type="error"
            handler={() => {
              handleRedirectToLandingPage();
            }}
          />
        )}

        <Box sx={{ mb: 4 }}>
          <Stepper separator="de" text="Dados pessoais" />
        </Box>
        <Typography sx={{ mb: 2 }} variant="h3">
          Digite o código recebido
        </Typography>
        <OpenTimePasswordMethod data={singnIn} />
        <TextfieldOneTimePassword
          onChange={value => {
            setOtp(value);
          }}
        />
      </Grid>
      <Grid item xs={12} sx={{ mb: 7 }}>
        <Typography sx={{ mb: 2 }} variant="body2R">
          Não recebeu?{' '}
          <Link component={'span'} variant="body2R" sx={{ cursor: 'pointer' }} onClick={() => retryCode(singnIn.method)}>
            Reenviar o código
          </Link>
        </Typography>

        <Button sx={{ mb: 1 }} variant="inline" color="primary" onClick={continueWithoutLogin}>
          Continuar sem código
        </Button>
        <br />

        <ToogleMethod
          method={singnIn.method}
          onClick={() => {
            sendCodeBy(singnIn.method === 'phone' ? 'email' : 'phone');
          }}
        />
      </Grid>
    </Grid>
  );
};

export default OpenTimePasswordForm;
